import React from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            QuickQuiz
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-4 font-medium">
            Interactive Quizzes Made Simple 🎉
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Create engaging quizzes in seconds. Share with friends, students, or colleagues.
            Watch results update in real-time. No installation required!
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 justify-center mb-16 max-w-2xl mx-auto">
          <Link
            to="/host"
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
          >
            🎯 Host a Quiz
          </Link>
          <Link
            to="/join"
            className="flex-1 bg-white text-purple-600 py-4 px-8 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-purple-600 text-center"
          >
            🎮 Join a Quiz
          </Link>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-4xl mb-4">⚡</div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">Lightning Fast</h3>
            <p className="text-gray-600">
              Create quizzes in seconds or generate random trivia. Get started instantly without any setup.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-4xl mb-4">🔄</div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">Real-Time Updates</h3>
            <p className="text-gray-600">
              Watch participants join and see answers come in live. Dynamic leaderboard updates automatically.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-4xl mb-4">📱</div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">Join Anywhere</h3>
            <p className="text-gray-600">
              Share quiz codes or QR codes. Participants join from any device with a browser. No app needed!
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">How It Works</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Hosts */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-blue-600 mb-4">👨‍🏫 For Hosts</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">1</span>
                  <p className="text-gray-700 pt-1">Create a new quiz or generate random trivia questions</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">2</span>
                  <p className="text-gray-700 pt-1">Share the quiz code or QR code with participants</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">3</span>
                  <p className="text-gray-700 pt-1">Start the quiz and watch live results come in</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">4</span>
                  <p className="text-gray-700 pt-1">View leaderboard and download results</p>
                </div>
              </div>
            </div>

            {/* For Participants */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-purple-600 mb-4">🎮 For Participants</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold">1</span>
                  <p className="text-gray-700 pt-1">Click "Join a Quiz" and enter your name</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold">2</span>
                  <p className="text-gray-700 pt-1">Enter the quiz code shared by the host</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold">3</span>
                  <p className="text-gray-700 pt-1">Answer questions as they appear</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold">4</span>
                  <p className="text-gray-700 pt-1">See your score and ranking on the leaderboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <div className="text-3xl font-bold text-blue-600 mb-1">∞</div>
            <div className="text-sm text-gray-600">Unlimited Quizzes</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <div className="text-3xl font-bold text-purple-600 mb-1">⚡</div>
            <div className="text-sm text-gray-600">Real-Time Results</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <div className="text-3xl font-bold text-pink-600 mb-1">📊</div>
            <div className="text-sm text-gray-600">Live Leaderboards</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <div className="text-3xl font-bold text-green-600 mb-1">🎯</div>
            <div className="text-sm text-gray-600">Easy to Use</div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg mb-6 opacity-90">Create your first quiz or join an existing one in seconds!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/host"
              className="bg-white text-purple-600 py-3 px-8 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Create a Quiz
            </Link>
            <Link
              to="/help"
              className="bg-purple-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-800 transition-colors border-2 border-white"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

